import { api, type FetchOptions, type MutOpts } from "@/api"
import { queryOptions, useMutation, useQuery } from "@tanstack/react-query"

import { queryClient } from "@/lib/query-client"

import type { components, paths } from "@/api/v1"

export type AdtTriggerRule = components["schemas"]["PathwayTriggerRule"]

export type AdtTriggerRulesListResponse =
  paths["/adt/trigger-rules/"]["get"]["responses"][200]["content"]["application/json"]

const adtTriggerRuleKeys = {
  all: ["adt-trigger-rules"] as const,
  list: (query?: Record<string, unknown>) =>
    [...adtTriggerRuleKeys.all, "list", query] as const,
  create: () => [...adtTriggerRuleKeys.all, "create"] as const,
  update: () => [...adtTriggerRuleKeys.all, "update"] as const,
  delete: () => [...adtTriggerRuleKeys.all, "delete"] as const,
}

export type AdtTriggerRulesListQuery = {
  facility?: number
  isActive?: boolean
  /** Filter by trigger discriminator */
  triggerType?: components["schemas"]["TriggerTypeEnum"]
  page?: number
  pageSize?: number
}

export const adtTriggerRulesQueryOptions = (query?: AdtTriggerRulesListQuery) =>
  queryOptions({
    queryKey: adtTriggerRuleKeys.list(query ?? {}),
    queryFn: async (): Promise<AdtTriggerRulesListResponse | undefined> => {
      const { data } = await api.GET("/adt/trigger-rules/", {
        params: { query },
      })
      return data
    },
    staleTime: 30_000,
  })

export const useAdtTriggerRulesQuery = (query?: AdtTriggerRulesListQuery) => {
  return useQuery(adtTriggerRulesQueryOptions(query))
}

export const useCreateAdtTriggerRuleMutation = (
  mutationOptions?: MutOpts<"post", "/adt/trigger-rules/">
) => {
  return useMutation({
    mutationKey: adtTriggerRuleKeys.create(),
    mutationFn: (fetchOpts: FetchOptions<"post", "/adt/trigger-rules/">) =>
      api.POST("/adt/trigger-rules/", fetchOpts),
    ...mutationOptions,
    onSuccess: (...args) => {
      void queryClient.invalidateQueries({ queryKey: adtTriggerRuleKeys.all })
      return mutationOptions?.onSuccess?.(...args)
    },
  })
}

export const useUpdateAdtTriggerRuleMutation = (
  mutationOptions?: MutOpts<"patch", "/adt/trigger-rules/{id}/">
) => {
  return useMutation({
    mutationKey: adtTriggerRuleKeys.update(),
    mutationFn: (fetchOpts: FetchOptions<"patch", "/adt/trigger-rules/{id}/">) =>
      api.PATCH("/adt/trigger-rules/{id}/", fetchOpts),
    ...mutationOptions,
    onSuccess: (...args) => {
      void queryClient.invalidateQueries({ queryKey: adtTriggerRuleKeys.all })
      return mutationOptions?.onSuccess?.(...args)
    },
  })
}

export const useDeleteAdtTriggerRuleMutation = (
  mutationOptions?: MutOpts<"delete", "/adt/trigger-rules/{id}/">
) => {
  return useMutation({
    mutationKey: adtTriggerRuleKeys.delete(),
    mutationFn: (fetchOpts: FetchOptions<"delete", "/adt/trigger-rules/{id}/">) =>
      api.DELETE("/adt/trigger-rules/{id}/", fetchOpts),
    ...mutationOptions,
    onSuccess: (...args) => {
      void queryClient.invalidateQueries({ queryKey: adtTriggerRuleKeys.all })
      return mutationOptions?.onSuccess?.(...args)
    },
  })
}
